import clsx from 'clsx';
import { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { SharedLinkProps } from '../../types/SharedLinkProps';

import { useLinkElement } from '../../context/LinkNavigation/LinkNavigation';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import './Link.css';

const messages = defineMessages({
  externalLinkIconLabel: {
    id: 'Link.launchTooltip',
    defaultMessage: 'Opens in a new tab',
    description: 'Tooltip for launch icon indicating link opens in a new tab',
  },
});

export const Link = memo((props: SharedLinkProps) => {
  const intl = useIntl();
  const externalLinkIconLabel = intl.formatMessage(
    messages.externalLinkIconLabel,
  );
  const {
    external = false,
    href,
    text,
    size = 'm',
    variant = 'default',
  } = props;

  // External links stay native anchors; in-app links route through the app.
  const LinkElement = useLinkElement(href, external ? '_blank' : undefined);

  const linkContent = external ? (
    <Layout align="center" spacing="xs">
      {text}
      <Icon icon="open-external-link" label={externalLinkIconLabel} size="s" />
    </Layout>
  ) : (
    text
  );

  return (
    <LinkElement
      className={clsx(
        'eds-link',
        `eds-link--variant-${variant}`,
        `eds-link--${size}`,
        external && 'eds-link--external',
      )}
      href={href}
      rel={external ? 'noopener noreferrer' : undefined}
      target={external ? '_blank' : undefined}
    >
      {linkContent}
    </LinkElement>
  );
});

Link.displayName = 'Link';
